import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, SimpleChanges } from '@angular/core';
import { CustomizerComponentHeaderComponent } from '@app/customizer/components';
import { MenuOverlayComponent } from '@app/shared/components/menu-overlay/menu-overlay';
import { ComponentModel } from '@models/component';
import { ComponentVariantChangeModel } from '@models/component-variant-change';
import { ConfigurationService } from '@services/configuration.service';
import { DataApiService } from '@services/data-api.service';
import { EventsService } from '@services/events.service';
import { ImageService } from '@services/image.service';
import { LanguageService } from '@services/language.service';
import { ComponentCodeConstants } from '@shared/component-constants';
import { CustomizerStateConstants } from '@shared/customizer-state-constants';
import { ComponentVariantFromApiModel } from '../../../../_models/api-models/component-variant-fromapi';
import { SorterService } from '../../../../_services/sorter.service';
import { AngleConstants } from '../../../../shared/angle-constants';
import { BaseCustomizerComponent } from '../../../../shared/base-customizer-component';
import { FinishModel } from '../components-variants-grouped';

@Component({
	template: `
    <customizer-component-header [menu]="menu" (updateCollapsedOnComponent)="updateCollapsed($event)"
      [overrideTranslateName]="finishHeader">
      <div class="finishes">
        @for (finish of finishes; track finish) {
          <div class="customizer-item no-pointer"
            [ngClass]="{'is-selected': (selectedFinish && selectedFinish.code === finish.code)}"
            >
            <div class="inner-column pointer pl-5" (click)="onSelected(finish)">
              @if (getFinishImage(finish.finishIndicator)) {
                <div class="image">
                  <img class="dcg-image" [src]="getFinishImage(finish.finishIndicator)" />
                </div>
              }
              <h6 class="m-0">{{finish.name}}</h6>
            </div>
            @if (selectedFinish?.code === finish.code && showOverlay) {
              <menu-overlay
                [secondOverlayText]="secondOverlayText">
              </menu-overlay>
            }
          </div>
        }
        @if (helpText) {
          <div class="help-text" [innerHTML]="helpText"></div>
        }
      </div>
    </customizer-component-header>
    `,
	imports: [CustomizerComponentHeaderComponent, CommonModule, MenuOverlayComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './finish.component.scss',

})
export class FinishNosepadComponent extends BaseCustomizerComponent implements OnInit {

	public designs: Array<ComponentVariantFromApiModel> = [];
	public finishes: Array<FinishModel> = [];
	public selectedFinish: FinishModel;
	public finishHeader: string = '';
	public helpText: string | null = null;
	public secondOverlayText: string | null = null;
	public editMode: boolean = false;
	public showOverlay: boolean = false;

	constructor(
		public override events: EventsService,
		private imageService: ImageService,
		public apiService: DataApiService,
		public sorter: SorterService,
		public languageService: LanguageService,
		public override configurationService: ConfigurationService
	) {
		super(events, apiService, configurationService);
	}

	async initData() {
		this.events.showLoader(true);
		this.editMode = this.state === CustomizerStateConstants.EDITORDER;
		await this.fetchData();
		this.setImageSliderByName(AngleConstants.Perspective);

		this.events.showLoader(false);
	}

	onChanges(changes: SimpleChanges): void {
		if (this.getSelectedVariant && this.getSelectedVariant.Code) {
			// selectedVariantFinishIndicator should be 'pol', or 'sat' or another three letter combo
			let selectedVariantFinishIndicator = this.getSelectedVariant.Code.substring(this.getSelectedVariant.Code.indexOf(';'), this.getSelectedVariant?.Code.length);

			if (selectedVariantFinishIndicator) {
				this.selectedFinish = this.finishes.filter(f => f.finishIndicator === selectedVariantFinishIndicator)[0];
			}
			else {
				// getSelectedVariant apparently null/undefined thus making the indicator null aswell
			}
		}
		else {
			// getSelectedVariant is not supposed to be null/undefined
		}
	}

	getImage = (variant: ComponentVariantFromApiModel) => this.imageService.GetVariantImage(variant.ToComponentVariantModel(""));

	getFinishImage = (finish: string | undefined) => this.imageService.GetFinishImage(finish);

	async fetchData() {
		const currentActiveFinish = this.variants.filter(v => v.Component.Uuid === this.menu.menuComponents[0].ComponentUuids)[0];

		const components: Array<ComponentModel> = [];

		for (let index = 0; index < this.menu.menuComponents.length; index++) {
			const menuComponent = this.menu.menuComponents[index];

			if (this.designs.filter(f => f.Component.Uuid === menuComponent.ComponentUuids).length === 0) {
				let variantsForComponent = await this.configurationService.getVariants(this.configuration?.ModelConfigurationUuid, this.modelGroup, menuComponent.ComponentUuids);
				variantsForComponent = variantsForComponent?.sort(this.sorter.sortBy(ComponentVariantFromApiModel));

				if (variantsForComponent?.[0] && variantsForComponent[0].Component.Code === ComponentCodeConstants.Nosepad) {
					const currentActiveFinishTemple = this.variants.filter(v => v.Component.Code === ComponentCodeConstants.Nosepad)[0];
					let variantIndicator = currentActiveFinishTemple.Code.substring(0, currentActiveFinishTemple.Code.indexOf(';'));
					variantsForComponent = variantsForComponent.filter(v => v.Code.indexOf(variantIndicator) > -1);

					variantsForComponent.forEach(variant => {
						let name = this.languageService.instantTranslateData(variant.Description);

						if (variant.Description2 && this.languageService.instantTranslateData(variant.Description2)) {
							name = this.languageService.instantTranslateData(variant.Description2);
						}

						const finishIndicator = variant.Code.substring(variant.Code.indexOf(';'), variant.Code.length);

						if (components.filter(c => c.Id === variant.Component.Id).length === 0) {
							components.push(variant.Component);
						}

						if (finishIndicator.indexOf(';') > -1) {
							if (this.finishes.filter(f => f.finishIndicator === finishIndicator).length === 0) {
								this.finishes.push({ code: variant.Code, name: name, components: components, finishIndicator: finishIndicator })
							}
						}
					});

					const firstOrDefaultVariant = variantsForComponent.filter(d => d.Default)[0] || variantsForComponent[0];
					this.designs.push(firstOrDefaultVariant);
				}
			}
		}

		const finishIndicator = currentActiveFinish.Code.substring(currentActiveFinish.Code.indexOf(';'), currentActiveFinish.Code.length);

		this.selectedFinish = this.finishes.filter(f => f.finishIndicator === finishIndicator)[0];

		if (currentActiveFinish) {
			this.selectedFinish = {
				code: currentActiveFinish.Code,
				name: this.languageService.instantTranslateData(currentActiveFinish.Description),
				components: undefined,
				finishIndicator: undefined
			};
		}

		await this.RunDisablingSystem(this.designs);

		this.finishHeader = this.languageService.instant('CUSTOMIZER.COMPONENTS.FINISH.NAME');
	}

	async onSelected(finish: FinishModel): Promise<boolean> {
		let returnMessage = null;
		let finishesChangeModels: Array<ComponentVariantChangeModel> = [];
		let updateVariants = true;

		returnMessage = `${this.languageService.instant('CUSTOMIZER.COMPONENTS.FINISH.WASSELECTED', { finish: finish.name.toLowerCase() })}:<br />`;

		let colorMenu = (await this.menuService.getMenu(this.modelGroup))?.filter(m => m.Name === 'Color')[0];
		if (finish.components)
			for (let index = 0; index < finish.components.length; index++) {
				const component = finish.components[index];

				const linkedMenuItem = colorMenu?.SubMenus.filter(s => s.Components.indexOf(component.Code) > -1)[0];

				if (linkedMenuItem) {
					this.updateMenuVisited(linkedMenuItem);
				}

				let variantsForComponent = await this.configurationService.getVariants(this.configuration?.ModelConfigurationUuid, this.modelGroup, component.Uuid);

				let variantsByFinish = variantsForComponent?.filter(a => finish.finishIndicator && a.Code.indexOf(finish.finishIndicator) > -1);

				updateVariants = (variantsByFinish?.length ?? 0) > 0;

				if (updateVariants) {
					let variantToSelect = variantsByFinish?.[0];
					const variantCurrentlySelected = this.variants.filter(v => v.Component.Uuid === component.Uuid)[0];

					if (component.Code === ComponentCodeConstants.Temple) {
						let templeDesignIndicator = variantCurrentlySelected.Code.substring(0, variantCurrentlySelected.Code.indexOf(';'));
						variantToSelect = variantsByFinish?.filter(d => d.Code.indexOf(templeDesignIndicator) > -1)[0];
					}

					returnMessage += `&middot; ${variantToSelect?.Component.Description.toLowerCase()}<br />`;

					const newVariant = variantToSelect?.ToComponentVariantModel((this.getSelectedVariant ? this.getSelectedVariant.Engraving : ''), (this.getSelectedVariant ? this.getSelectedVariant.OrderLineUuid : ''), true);
					finishesChangeModels.push(new ComponentVariantChangeModel(variantCurrentlySelected, newVariant, variantToSelect?.VariantSizes));
				}
			};

		if (updateVariants && finishesChangeModels.length > 0) {
			for (let index = 0; index < finishesChangeModels.length; index++) {
				const finishesChangeModel = finishesChangeModels[index];

				this.updateVariantDesign(finishesChangeModel);
			};

			if (finishesChangeModels.length === 1) {
				returnMessage = null;
			}
		}

		if (this.editMode) {
			this.showOverlay = true;
			this.secondOverlayText = this.languageService.instant('CUSTOMIZER.CHANGES.BAGUPDATED');

			new Promise(resolve => setTimeout(resolve, 2500)).then(x => {
				this.showOverlay = false;
			});
		}

		this.helpText = returnMessage;

		return updateVariants;
	}

}
